import React, { useCallback, useEffect, useState } from "react";
import { Check, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { ActionIcon } from "./ActionIcon";
import { Edit3 } from "lucide-react";

/** Assigns the J.P. Morgan Access payment template each employer uses to pay each vendor. */
export default function VendorTemplates() {
  const [rows, setRows] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [template, setTemplate] = useState("");
  const [pending, setPending] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/vendors");
    setRows(data);
  }, []);

  useEffect(() => { load().catch((e) => toast.error(formatApiError(e.response?.data?.detail))); }, [load]);

  const requestCode = async (row) => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/step-up/request", { purpose: "payment" });
      setPending({ row, template, step_up_token: data.step_up_token });
      setCode("");
      toast.success(`Verification code sent to ${data.email_hint}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/step-up/verify", {
        step_up_token: pending.step_up_token, code,
      });
      await api.put(`/admin/vendors/${pending.row.id}/payment-template`, {
        payment_template: pending.template, grant: data.grant,
      });
      toast.success(pending.template ? "Payment template saved" : "Payment template cleared");
      setPending(null);
      setEditingId(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const missing = rows.filter((row) => !row.payment_template).length;

  return (
    <div className="mt-12" data-testid="vendor-templates">
      <h2 className="font-heading font-bold text-2xl mb-1" style={{ color: "var(--ink)" }}>Payment templates</h2>
      <p className="text-sm max-w-2xl mb-5" style={{ color: "var(--muted)" }}>
        Each employer pays a vendor through a pre-defined template in J.P. Morgan Access. Enter the
        template name exactly as it appears in Access — the portal submits the payment against it and
        your second administrator approves the transaction inside Access. The same vendor name can
        appear for multiple employers; each employer assignment keeps its own template.
        {missing ? ` ${missing} vendor assignment(s) still need a template.` : ""}
      </p>

      <div className="border max-h-[460px] overflow-auto" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="vendor-templates-region">
        <table className="w-full text-sm min-w-[720px]" data-testid="vendor-templates-table">
          <thead>
            <tr className="border-b eyebrow text-left" style={{ borderColor: "var(--border)" }}>
              <th className="px-4 py-3">Employer</th>
              <th className="px-4 py-3">Vendor</th>
              <th className="px-4 py-3">Access payment template</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-b" style={{ borderColor: "var(--border)" }} data-testid={`template-row-${row.id}`}>
                <td className="px-4 py-3" style={{ color: "var(--ink)" }}>{row.company}</td>
                <td className="px-4 py-3 font-medium" style={{ color: "var(--ink)" }}>{row.name}</td>
                <td className="px-4 py-3" data-testid={`template-value-${row.id}`}>
                  {editingId === row.id ? (
                    <input className="input-flat" autoFocus placeholder="e.g. NOOM-BSH-ACH"
                      value={template} onChange={(e) => setTemplate(e.target.value)}
                      data-testid={`template-input-${row.id}`}/>
                  ) : row.payment_template ? (
                    <span className="font-mono text-xs" style={{ color: "var(--ink)" }}>{row.payment_template}</span>
                  ) : (
                    <span className="text-xs" style={{ color: "#B45309" }}>Not assigned</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  {editingId === row.id ? (
                    <span className="inline-flex gap-1">
                      <ActionIcon icon={Check} label="Email a code and save" onClick={() => requestCode(row)} testId={`save-template-${row.id}`}/>
                      <ActionIcon icon={X} label="Cancel" onClick={() => setEditingId(null)} testId={`cancel-template-${row.id}`}/>
                    </span>
                  ) : (
                    <ActionIcon icon={Edit3} label="Assign payment template"
                      onClick={() => { setEditingId(row.id); setTemplate(row.payment_template || ""); }}
                      testId={`edit-template-${row.id}`}/>
                  )}
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={4} className="p-10 text-center text-sm" style={{ color: "var(--muted)" }} data-testid="vendor-templates-empty">
                No employer vendor assignments yet.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {pending && (
        <form onSubmit={save} className="border p-6 mt-6 max-w-md" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="template-step-up">
          <h3 className="font-heading font-bold text-lg mb-1" style={{ color: "var(--ink)" }}>Confirm with your code</h3>
          <p className="text-xs mb-5" style={{ color: "var(--muted)" }}>
            Payment detail changes need a fresh email code. Enter the 6-digit code we sent you to set
            “{pending.template || "no template"}” for {pending.row.name} at {pending.row.company}.
          </p>
          <input className="input-flat font-mono tracking-[0.4em]" inputMode="numeric" maxLength={6}
            value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            data-testid="template-step-up-code"/>
          <div className="flex gap-3 mt-5">
            <button type="submit" disabled={busy || code.length !== 6} className="btn-primary" data-testid="template-step-up-confirm">
              {busy ? "Saving…" : "Confirm change"}
            </button>
            <button type="button" onClick={() => setPending(null)} className="btn-ghost" data-testid="template-step-up-cancel">
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
